import { Accordion, accordionClasses } from '@naf-simba/elements-accordion';
import { simbaStyled, type Theme } from '@naf-simba/theme';
import { nafColor } from '@nafcore/theme';
import { isNotTransientOrSystemProp } from '../../../lib/shouldForwardProp';
import { MembershipAccordionVariant } from '../Types';

// neutral3 and neutral1 have no Simba equivalent, so the dimmed titles stay on the NAF palette.
const titleColor = (theme: Theme, variant?: MembershipAccordionVariant) => {
  switch (variant) {
    case MembershipAccordionVariant.Available:
      return theme.palette.simba.colors.common.black;
    case MembershipAccordionVariant.Unavailable:
      return nafColor.neutral.neutral3;
    default:
      return nafColor.neutral.neutral1;
  }
};

// The slot matches the medium start icon; the ring is centered in it so the title text stays aligned with the check.
const MARKER_SLOT = '1.5rem';
const MARKER_SIZE = '1.25rem';
const MARKER_INSET = `calc((${MARKER_SLOT} - ${MARKER_SIZE}) / 2)`;

const MINUS_ICON =
  "url(\"data:image/svg+xml,%3Csvg width='10' height='3' viewBox='0 0 10 2' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0' y='0.5' width='10' height='1' fill='%23C1C1C1'/%3E%3C/svg%3E\")";

type StyledAccordionProps = {
  $variant?: MembershipAccordionVariant;
  $showIcon?: boolean;
  $noBackground?: boolean;
};

export const StyledAccordion = simbaStyled<typeof Accordion, StyledAccordionProps>(Accordion, {
  shouldForwardProp: isNotTransientOrSystemProp,
})(({ theme, $variant, $showIcon = true, $noBackground }) => ({
  // Badge and title on the left, chevron on the right; the badge sets its own spacing.
  '&& button': {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 0,
    padding: `${theme.spacing('m')} ${theme.spacing('ml')} ${theme.spacing('m')} ${theme.spacing('l')}`,
    color: titleColor(theme, $variant),
  },

  [`&& .${accordionClasses.chevron}`]: {
    marginLeft: 'auto',
    alignSelf: 'center',
    color: theme.palette.simba.colors.common.black,
  },

  // The checked marker is the accordion's start icon, in the green of TextListItem's checked variant.
  [`&& .${accordionClasses.startIcon}`]: {
    alignSelf: 'flex-start',
    alignItems: 'center',
    height: '1.25rem',
    color: theme.palette.simba.colors.secondary[500],
  },

  // The unavailable marker before the title: a minus in a grey ring.
  ...($showIcon &&
    $variant !== MembershipAccordionVariant.Available && {
      '&& button::before': {
        content: "''",
        boxSizing: 'border-box',
        flex: `0 0 ${MARKER_SIZE}`,
        width: MARKER_SIZE,
        height: MARKER_SIZE,
        marginTop: `calc((1.25rem - ${MARKER_SIZE}) / 2)`,
        marginLeft: MARKER_INSET,
        marginRight: `calc(${MARKER_INSET} + ${theme.spacing('m')})`,
        borderRadius: '50%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '10px',
        border: `2px solid ${nafColor.neutral.neutral3}`,
        backgroundImage: MINUS_ICON,
      },
    }),

  ...(!$noBackground && {
    "&:has(button[aria-expanded='true'])": {
      backgroundColor: theme.palette.simba.colors.secondary[100],
    },
  }),

  "&& [role='region']": {
    padding: `0 ${theme.spacing('l')} ${theme.spacing('xl')}`,

    '& > :is(ul, ol):first-child': {
      paddingTop: theme.spacing('m'),
    },

    'p, ul, li, span': {
      fontSize: '1rem',
    },
  },
}));
